/**
 * 全屏与横屏。
 *
 * 浏览器的规矩：全屏必须由「用户手势」触发（点一下、按一下键），
 * 页面一打开就自己铺满全屏是不允许的 —— 这一点谁也绕不过去。
 * 所以退一步，做得尽量自然：
 *   1. 点「推门而入」的那一刻正好是用户手势，顺手进全屏，并试着把屏幕锁成横屏；
 *   2. 之后顶栏「全屏」按钮与 F 键随时切换；
 *   3. 浏览器不给全屏（比如 iPhone 上的 Safari）就给一句能照做的提示，不强求。
 */

const PREF_KEY = 'dongtianju.screen.fullscreen';   // 记着用户手动全屏过，下次接着来

export function isFullscreen() {
  return !!(document.fullscreenElement || document.webkitFullscreenElement);
}

/** iOS 上的 Safari 不允许网页自己全屏，只能请用户把站点加到主屏幕 */
function isIosSafari() {
  const ua = navigator.userAgent || '';
  const ios = /iPad|iPhone|iPod/.test(ua)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return ios && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

export class ScreenCtl {
  /**
   * @param {{toast?: (text: string, kind?: string) => void}} [opts]
   */
  constructor(opts = {}) {
    this.toast = opts.toast || (() => {});
    this.btn = document.getElementById('btn-full');
    this.lab = this.btn && this.btn.querySelector('.lab');
    this._lockTried = false;

    if (this.btn) this.btn.onclick = () => this.toggle();
    window.addEventListener('keydown', (e) => {
      if (e.key !== 'f' && e.key !== 'F') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      // 别在输入框里抢键（书架、串门、电视都要打字）
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      this.toggle();
    });
    document.addEventListener('fullscreenchange', () => this.sync());
    document.addEventListener('webkitfullscreenchange', () => this.sync());
    this.sync();
  }

  sync() {
    if (this.lab) this.lab.textContent = isFullscreen() ? '退出' : '全屏';
    if (this.btn) this.btn.classList.toggle('on', isFullscreen());
    if (isFullscreen()) { try { localStorage.setItem(PREF_KEY, '1'); } catch (e) { /* 无痕模式 */ } }
  }

  /** 进全屏；已经在全屏里就只补一次横屏锁定 */
  async enter() {
    if (!isFullscreen()) {
      const el = document.documentElement;
      // iPhone 的 Safari 根本没有这个方法，直接给提示，别白试
      if (!el.requestFullscreen && !el.webkitRequestFullscreen) {
        this.hintUnsupported();
        return false;
      }
      try {
        // navigationUI: hide —— 手机上连返回条一起藏掉，画面才是完整的一幅
        const req = (el.requestFullscreen || el.webkitRequestFullscreen).bind(el);
        await req({ navigationUI: 'hide' });
      } catch (err) {
        try { await (el.requestFullscreen || el.webkitRequestFullscreen).call(el); }
        catch (err2) { this.hintUnsupported(err2); return false; }
      }
    }
    await this.lockLandscape();
    return isFullscreen();
  }

  async exit() {
    const fn = document.exitFullscreen || document.webkitExitFullscreen;
    if (fn) { try { await fn.call(document); } catch (e) { /* 用户自己按了 Esc 也算 */ } }
    this.unlockOrientation();
  }

  toggle() { return isFullscreen() ? this.exit() : this.enter(); }

  /** 试着把屏幕锁成横屏：安卓 Chrome 可以，iOS Safari 不给，失败就当没说 */
  async lockLandscape() {
    const so = screen.orientation;
    if (!so || typeof so.lock !== 'function') return false;
    try { await so.lock('landscape'); this._lockTried = true; return true; }
    catch (e) { return false; }
  }

  unlockOrientation() {
    const so = screen.orientation;
    if (so && typeof so.unlock === 'function') { try { so.unlock(); } catch (e) { /* 无关紧要 */ } }
  }

  hintUnsupported() {
    if (isIosSafari() && !window.navigator.standalone) {
      this.toast('iPhone 上请用 Safari 的「分享 → 添加到主屏幕」，从桌面图标进就是全屏', 'warn');
    } else {
      this.toast('这个浏览器不让网页自己全屏，请按 F11 或用顶栏「全屏」', 'warn');
    }
  }

  /** 用户曾经手动全屏过 -> 下次进门时自动铺满（否则桌面上不打扰） */
  static wanted() {
    try { return localStorage.getItem(PREF_KEY) === '1'; } catch (e) { return false; }
  }
}
